// Wizard draft state + autosave. The draft is held in local state (fast writes),
// and a debounced save pushes it through RTK Query. The dirty flag lives in
// Redux so the unsaved-changes prompt can observe it from outside the wizard.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useCreateCampaignMutation,
  useGetCampaignQuery,
  useUpdateCampaignMutation,
} from '../campaignsApi';
import { createEmptyDraft } from '../utils/draftDefaults';
import { markWizardSaved, setWizardDirty } from '../campaignsSlice';
import { LIMITS } from '../constants';

export const useCampaignDraft = (campaignId) => {
  const dispatch = useDispatch();
  const { data: existing, isLoading } = useGetCampaignQuery(campaignId, { skip: !campaignId });

  const [draft, setDraft] = useState(() => createEmptyDraft());
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const dirtyRef = useRef(false);
  const pendingTimer = useRef(null);
  const draftRef = useRef(draft);
  const isMountedRef = useRef(true);

  // Keep a ref to the latest draft so persist() can tell if the user has
  // typed anything between request dispatch and response.
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const [createCampaign] = useCreateCampaignMutation();
  const [updateCampaign] = useUpdateCampaignMutation();

  // Hydrate from loaded campaign on edit.
  useEffect(() => {
    if (existing && campaignId) {
      setDraft(existing);
      dirtyRef.current = false;
      dispatch(setWizardDirty(false));
    }
  }, [existing, campaignId, dispatch]);

  const persist = useCallback(async () => {
    if (!dirtyRef.current) return null;
    const snapshot = draftRef.current;
    setIsSaving(true);
    try {
      const result = snapshot.id
        ? await updateCampaign(snapshot).unwrap()
        : await createCampaign(snapshot).unwrap();
      if (!isMountedRef.current) return result;
      // If the user hasn't edited anything since we dispatched, take the server's
      // response wholesale. If they have, only graft the server-owned fields onto
      // their latest local state — replacing wholesale would clobber their edits.
      const stillClean = draftRef.current === snapshot;
      if (stillClean) {
        setDraft(result);
        dirtyRef.current = false;
        dispatch(markWizardSaved());
      } else {
        setDraft((prev) => ({
          ...prev,
          id: prev.id ?? result.id,
          createdAt: prev.createdAt ?? result.createdAt,
          updatedAt: result.updatedAt,
        }));
      }
      setLastSavedAt(new Date().toISOString());
      return result;
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  }, [createCampaign, updateCampaign, dispatch]);

  const scheduleAutosave = useCallback(() => {
    if (pendingTimer.current) clearTimeout(pendingTimer.current);
    pendingTimer.current = setTimeout(() => {
      persist().catch(() => { /* surfaced via toast elsewhere */ });
    }, LIMITS.AUTOSAVE_INTERVAL_MS);
  }, [persist]);

  useEffect(() => () => pendingTimer.current && clearTimeout(pendingTimer.current), []);

  const update = useCallback((patch) => {
    setDraft((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      return next;
    });
    dirtyRef.current = true;
    dispatch(setWizardDirty(true));
    scheduleAutosave();
  }, [dispatch, scheduleAutosave]);

  const saveNow = useCallback(async () => {
    if (pendingTimer.current) clearTimeout(pendingTimer.current);
    return persist();
  }, [persist]);

  // Flush on blur — consumed by the wizard body. Same contract as the 10s timer.
  const flushOnBlur = useCallback(() => {
    if (pendingTimer.current) clearTimeout(pendingTimer.current);
    persist().catch(() => {});
  }, [persist]);

  return useMemo(
    () => ({
      draft,
      update,
      saveNow,
      flushOnBlur,
      isLoading,
      isSaving,
      lastSavedAt,
      isDirty: dirtyRef.current,
    }),
    [draft, update, saveNow, flushOnBlur, isLoading, isSaving, lastSavedAt],
  );
};
